import React, { useState } from 'react';
import SendIcon from '@mui/icons-material/Send';
import _ from 'lodash';
import { Grid } from '@mui/material';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import PushForm from '../PushForm';
import PushPreviewer from '../previewer/PushPreviewer';
import DashboardLayout from '../../../../layouts/Dashboard';
import { mobilizeiPushNotificationsService } from '../../../../services/mobilizei/pushNotifications.service';
import { useUser } from "../../../../contexts/user.context";
import { showApiError } from '../../../../utils/errors';

export default function PushNew() {
    const { user } = useUser();
    const navigate = useNavigate();
    const location = useLocation();

    // When coming from the "Duplicate" action, prefill the form with the
    // original notification's content (never its schedule/status).
    const duplicateFrom = location.state?.duplicateFrom;
    const isEditing = location.state?.isEditing

    const [payload, setPayload] = useState(() => {
        if (duplicateFrom) {
            return {
                title: duplicateFrom.title || '',
                text: duplicateFrom.text || '',
                action: duplicateFrom.action || 'noAction',
                link: duplicateFrom.link || '',
                linkName: duplicateFrom.linkName || '',
                productUrl: duplicateFrom.productUrl || '',
                image: duplicateFrom.image || undefined,
                showPushModal: duplicateFrom.showPushModal || false,
            };
        }

        return { title: '', text: '', action: 'noAction' };
    });

    const [sendType, setSendType] = useState(isEditing ? duplicateFrom.sendType : 'now');
    const [scheduledFor, setScheduledFor] = useState(isEditing ? duplicateFrom.scheduledFor : '');
    const [isSending, setIsSending] = useState(false);

    const handlePayloadChange = (propertyArray) => {
        let newPayload = JSON.parse(JSON.stringify(payload));

        for (const item of propertyArray) {
            _.set(newPayload, item.property, item.value);

            if (item.property === 'action') {
                _.set(newPayload, 'link', '');
                _.set(newPayload, 'productUrl', '');
            }
        }

        setPayload(newPayload);
    }

    const handleScheduleChange = ({ sendType: newSendType, scheduledFor: newScheduledFor }) => {
        if (newSendType !== undefined) setSendType(newSendType);
        if (newScheduledFor !== undefined) setScheduledFor(newScheduledFor);
    }

    function handleSend() {
        setIsSending(true);

        const body = { ...payload };

        // The form holds a local datetime; the API expects ISO/UTC.
        if (sendType === 'scheduled' && scheduledFor) {
            body.scheduledFor = new Date(scheduledFor).toISOString();
        }

        mobilizeiPushNotificationsService.sendPushNotification(
            user?.store?.platformId,
            user?.store?.id,
            body
        )
            .then(() => {
                toast.success(sendType === 'scheduled'
                    ? 'Notificação agendada com sucesso!'
                    : 'Notificação enviada com sucesso!');
                navigate('/push');
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsSending(false);
            })
    }

    function handleEdit() {
        setIsSending(true);

        const body = { ...payload };

        if (sendType === 'scheduled' && scheduledFor) {
            body.scheduledFor = new Date(scheduledFor).toISOString();
        }

        mobilizeiPushNotificationsService.editPushNotification(
            user?.store?.platformId,
            user?.store?.id,
            duplicateFrom.id,
            body
        )
            .then(() => {
                toast.success('Notificação salva com sucesso!');
                navigate('/push');
            })
            .catch((e) => {
                showApiError(e?.error);
            })
            .finally(() => {
                setIsSending(false);
            })
    }

    return (
        <DashboardLayout
            Icon={SendIcon}
            title="Criar notificação push"
            subtitle="Escreva uma mensagem para os usuários do seu aplicativo e escolha entre enviar agora ou agendar para mais tarde"
        >
            <Grid container>
                <Grid item md={5} xs={12}>
                    <PushForm
                        isSending={isSending}
                        onPayloadChange={handlePayloadChange}
                        onSend={handleSend}
                        payload={payload}
                        showSendButton
                        showScheduling
                        sendType={sendType}
                        scheduledFor={scheduledFor}
                        onScheduleChange={handleScheduleChange}
                        isEditing={isEditing}
                        onEdit={handleEdit}
                    />
                </Grid>

                <Grid item md={7} xs={12}>
                    <PushPreviewer payload={payload} />
                </Grid>
            </Grid>
        </DashboardLayout>
    )
}
